import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import Swal from 'sweetalert2';
import DashboardSettingsForm from './DashboardSettingsForm';
import { getUserAllowedPlants } from '../services/PlantServices';
import {
  loadDashboardSettings,
  saveDashboardSettings,
} from '../utils/dashboardSettings';
import { isDashboardSettingsRoute } from '../utils/settingsRouteContext';

function DashboardSettingsOverlay() {
  const [cookies] = useCookies(['myToken', 'myUserId']);
  const location = useLocation();
  const showDashboardSettings = isDashboardSettingsRoute(location.pathname);
  const [showModal, setShowModal] = useState(false);
  const [allowedPlants, setAllowedPlants] = useState([]);
  const [dashboardSettings, setDashboardSettings] = useState(() =>
    loadDashboardSettings(cookies.myUserId)
  );
  const dashboardSettingsRef = useRef(null);

  useEffect(() => {
    if (!cookies.myToken || !showDashboardSettings) {
      return;
    }
    getUserAllowedPlants(cookies)
      .then((res) => {
        const plantOptions = (res?.plant_list || [])
          .filter((p) => p.status)
          .map((p) => ({ value: p.id, label: p.plant_alias }));
        setAllowedPlants(plantOptions);
      })
      .catch(() => setAllowedPlants([]));
    setDashboardSettings(loadDashboardSettings(cookies.myUserId));
  }, [cookies.myToken, cookies.myUserId, showDashboardSettings]);

  useEffect(() => {
    const onOpenContextualSettings = () => {
      if (!cookies.myToken || !showDashboardSettings) {
        return;
      }
      setDashboardSettings(loadDashboardSettings(cookies.myUserId));
      setShowModal(true);
    };
    window.addEventListener('openContextualSettings', onOpenContextualSettings);
    return () => window.removeEventListener('openContextualSettings', onOpenContextualSettings);
  }, [cookies.myToken, showDashboardSettings]);

  if (!cookies.myToken || !showDashboardSettings) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextDashboardSettings = dashboardSettingsRef.current?.getSettings?.();
    if (!nextDashboardSettings) {
      Swal.fire('Unable to read dashboard settings', '', 'error');
      return;
    }
    if (nextDashboardSettings.dateMode === 'date_range') {
      const from = nextDashboardSettings.customFromDate;
      const to = nextDashboardSettings.customToDate;
      if (!from || !to) {
        Swal.fire('Please select From and To dates', '', 'info');
        return;
      }
    }
    const saved = saveDashboardSettings(cookies.myUserId, nextDashboardSettings);
    setDashboardSettings(saved);
    setShowModal(false);
    Swal.fire('Dashboard settings updated', '', 'success');
  };

  return (
    <>
      {showModal ? <div className="modal-backdrop fade show" /> : null}
      <div
        className={`modal fade ${showModal ? 'show' : ''}`}
        style={{ display: showModal ? 'block' : 'none' }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-body display">
              <div className="container item-list-table-container">
                <form className="form" onSubmit={handleSubmit}>
                  <div id="header">
                    <h4 className="text font-weight-bold page-title">
                      General Settings for Dashboard
                    </h4>
                  </div>
                  <div className="container">
                    <DashboardSettingsForm
                      ref={dashboardSettingsRef}
                      allowedPlants={allowedPlants}
                      initialSettings={dashboardSettings}
                      showSectionHeader={false}
                    />
                  </div>
                  <div className="footer text-center">
                    <br />
                    <Button
                      type="submit"
                      style={{ width: '80px', fontWeight: 'bold', backgroundColor: 'RGB(42,245,39,0.8)' }}
                      className="btn btn-twitter"
                    >
                      Update
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                      type="button"
                      style={{ width: '80px', fontWeight: 'bold', backgroundColor: 'RGB(58,29,238,0.8)' }}
                      className="btn btn-twitter"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DashboardSettingsOverlay;
